import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, AlertTriangle, ArrowLeft, Calendar, Loader2, RotateCcw, Send, Star, Ticket, Wallet } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { EmptyState, Shimmer } from '@/components/ui';
import { AttachmentList, AttachmentPicker } from '@/components/support/Attachments';
import { useAuth } from '@/context/AuthContext';
import {
  getRefundPreview,
  getSupportCase,
  markSupportCaseRead,
  rateSupportCase,
  reopenSupportCase,
  requestRefund,
  sendSupportMessage,
} from '@/api/support';
import { friendlyError } from '@/lib/apiError';
import { formatINR } from '@/lib/catalog';
import {
  REFUND_STATUS,
  REFUND_TYPE_LABEL,
  caseStatus,
  dateTime,
  isClosedStatus,
  notifySupportRead,
  timeAgo,
} from '@/lib/support';

const POLL_MS = 15000;

/* ------------------------------------------------------------ Message */

function MessageBubble({ m }) {
  if (m.senderType === 'system') {
    return (
      <li className="flex justify-center">
        <p className="max-w-[90%] rounded-full bg-surface-sunken px-3 py-1 text-center text-xs text-ink-500">{m.message}</p>
      </li>
    );
  }
  const mine = m.mine ?? m.senderType === 'customer';
  const who = mine ? 'You' : m.senderType === 'agent' ? 'LocalKonnect Support' : m.senderName || 'Organiser';
  return (
    <li className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] sm:max-w-[75%]`}>
        <p className={`mb-1 text-[11px] font-semibold ${mine ? 'text-right text-ink-500' : m.senderType === 'organiser' ? 'text-warning' : 'text-brand-secondary'}`}>
          {who}
          {m.senderType === 'organiser' && <span className="font-normal text-ink-500"> · organiser</span>}
        </p>
        <div
          className={`rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed ${
            mine ? 'rounded-br-md bg-brand-secondary text-white' : 'rounded-bl-md bg-white text-ink-900 ring-1 ring-ink-200'
          }`}
        >
          {m.message && <p className="whitespace-pre-wrap break-words">{m.message}</p>}
          <AttachmentList items={m.attachments} onDark={mine} />
        </div>
        <p className={`mt-1 text-[11px] text-ink-500 ${mine ? 'text-right' : ''}`} title={dateTime(m.createdAt)}>
          {timeAgo(m.createdAt)}
        </p>
      </div>
    </li>
  );
}

/* --------------------------------------------------------- Refund box */

function RefundSection({ c, onChanged }) {
  const requests = c.refundRequests || [];
  const active = requests.find((r) => r.status === 'requested' || r.status === 'approved');
  const booking = c.booking;
  const closed = isClosedStatus(c.status);

  // What can be asked for depends on where the booking is.
  const types = !booking
    ? []
    : ['pending', 'confirmed', 'in_progress', 'payment_pending'].includes(booking.status)
      ? ['cancel_booking']
      : ['completed', 'cancelled', 'partially_cancelled'].includes(booking.status)
        ? ['full_refund']
        : [];

  const [open, setOpen] = useState(false);
  const [type, setType] = useState(types[0] || null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);

  const loadPreview = async (t) => {
    setType(t);
    setPreview(null);
    setPreviewing(true);
    try {
      setPreview(await getRefundPreview(c.id, t));
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't work out the refund for this booking."));
      setOpen(false);
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async () => {
    if (!type || reason.trim().length < 3) return;
    setSending(true);
    try {
      await requestRefund(c.id, { type, reason: reason.trim() });
      toast.success('Request sent — our team will review it');
      setOpen(false);
      setReason('');
      onChanged();
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't send your request."));
    } finally {
      setSending(false);
    }
  };

  if (!requests.length && (!types.length || closed)) return null;

  return (
    <div className="lk-card p-4 sm:p-5">
      <h2 className="flex items-center gap-2 text-sm font-bold text-ink-900">
        <Wallet className="h-4 w-4 text-ink-500" aria-hidden="true" />
        Refund &amp; cancellation
      </h2>

      {requests.length > 0 && (
        <ul className="mt-3 divide-y divide-ink-200/70">
          {requests.map((r) => {
            const st = REFUND_STATUS[r.status] || { label: r.status, tone: 'text-ink-700' };
            const amount = r.approvedAmount ?? r.policyAmount ?? r.requestedAmount;
            return (
              <li key={r.id} className="py-2.5 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-ink-900">{REFUND_TYPE_LABEL[r.type] || r.type}</span>
                  {amount != null && <span className="font-semibold text-ink-900">₹{formatINR(amount)}</span>}
                </div>
                <p className={`mt-0.5 text-xs font-semibold ${st.tone}`}>{st.label}</p>
                {r.decisionNote && <p className="mt-1 text-xs text-ink-500">{r.decisionNote}</p>}
              </li>
            );
          })}
        </ul>
      )}

      {!active && !closed && types.length > 0 && !open && (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            loadPreview(types[0]);
          }}
          className="lk-btn-ghost mt-3 w-full text-sm"
        >
          Request {types[0] === 'cancel_booking' ? 'cancellation & refund' : 'a refund'}
        </button>
      )}

      {open && (
        <div className="mt-3 space-y-3 rounded-xl bg-surface-muted p-3">
          {types.length > 1 && (
            <div className="flex gap-2">
              {types.map((t) => (
                <button key={t} type="button" onClick={() => loadPreview(t)} className={`lk-chip ${type === t ? 'lk-chip-active' : 'lk-chip-idle'}`}>
                  {REFUND_TYPE_LABEL[t]}
                </button>
              ))}
            </div>
          )}
          {previewing || !preview ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-ink-400" aria-label="Calculating" />
            </div>
          ) : (
            <>
              <div className="text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-500">You paid</span>
                  <span className="font-medium text-ink-900">₹{formatINR(preview.paid ?? preview.bookingTotal ?? 0)}</span>
                </div>
                <div className="mt-1 flex justify-between">
                  <span className="text-ink-500">Refund under the cancellation policy</span>
                  <span className="font-bold text-ink-900">₹{formatINR(preview.policyAmount ?? 0)}</span>
                </div>
                {preview.explanation && <p className="mt-2 text-xs leading-relaxed text-ink-500">{preview.explanation}</p>}
              </div>
              <div>
                <label htmlFor="refund-reason" className="mb-1 block text-xs font-medium text-ink-700">Why are you asking?</label>
                <textarea id="refund-reason" className="lk-input min-h-[5rem] resize-y" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
              </div>
              <p className="text-xs text-ink-500">
                Our team reviews every request. {type === 'cancel_booking' ? 'If approved, the booking is cancelled and the refund' : 'If approved, the refund'} goes back to your original payment method.
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setOpen(false)} className="lk-btn-ghost flex-1" disabled={sending}>
                  Not now
                </button>
                <button type="button" onClick={submit} disabled={sending || reason.trim().length < 3} className="lk-btn-primary flex-1 disabled:opacity-50">
                  {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send request
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- Rating */

function RatingBox({ c, onChanged }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);

  if (c.csatRating) {
    return (
      <div className="lk-card flex items-center gap-2 p-4 text-sm text-ink-700">
        <span>You rated this</span>
        <span className="flex" aria-label={`${c.csatRating} out of 5`}>
          {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} className={`h-4 w-4 ${n <= c.csatRating ? 'fill-warning text-warning' : 'text-ink-200'}`} aria-hidden="true" />
          ))}
        </span>
        <span>— thank you!</span>
      </div>
    );
  }
  if (!c.canRate) return null;

  const submit = async () => {
    if (!rating) return;
    setSending(true);
    try {
      await rateSupportCase(c.id, { rating, comment: comment.trim() || undefined });
      toast.success('Thanks for your feedback');
      onChanged();
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't save your rating."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="lk-card p-4 sm:p-5">
      <p className="text-sm font-bold text-ink-900">How did we do?</p>
      <div className="mt-2 flex gap-1" role="radiogroup" aria-label="Rate our support">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={rating === n}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            onClick={() => setRating(n)}
            className="lk-focus flex h-11 w-11 items-center justify-center rounded-full hover:bg-surface-muted"
          >
            <Star className={`h-7 w-7 ${n <= rating ? 'fill-warning text-warning' : 'text-ink-400'}`} aria-hidden="true" />
          </button>
        ))}
      </div>
      {rating > 0 && (
        <div className="mt-3 space-y-2">
          <textarea
            className="lk-input min-h-[4.5rem] resize-y"
            placeholder="Anything we could have done better? (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={1000}
            aria-label="Comment"
          />
          <button type="button" onClick={submit} disabled={sending} className="lk-btn-primary w-full">
            {sending && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit rating
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- Page */

const SupportCase = () => {
  const { id } = useParams();
  const { user, openAuthModal } = useAuth();
  const [c, setC] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [showReopen, setShowReopen] = useState(false);
  const endRef = useRef(null);
  const lastCount = useRef(0);

  const load = useCallback(
    async (quiet = false) => {
      if (!user) return;
      try {
        const data = await getSupportCase(id);
        setC(data);
        setLoadError('');
        const count = data?.messages?.length || 0;
        if (count !== lastCount.current) {
          lastCount.current = count;
          markSupportCaseRead(id)
            .then(notifySupportRead)
            .catch(() => {});
        }
      } catch (err) {
        if (!quiet) setLoadError(friendlyError(err, "We couldn't open this case."));
      }
    },
    [id, user]
  );

  useEffect(() => {
    lastCount.current = 0;
    setC(null);
    load();
  }, [load]);

  // Poll for replies while the tab is visible.
  useEffect(() => {
    if (!user) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [load, user]);

  const messageCount = c?.messages?.length || 0;
  useEffect(() => {
    if (messageCount) endRef.current?.scrollIntoView({ block: 'end' });
  }, [messageCount]);

  const send = async (e) => {
    e.preventDefault();
    if (!message.trim() && !attachments.length) return;
    setSending(true);
    try {
      await sendSupportMessage(id, { message: message.trim(), attachments });
      setMessage('');
      setAttachments([]);
      await load(true);
    } catch (err) {
      toast.error(friendlyError(err, "Your message wasn't sent. Please try again."));
    } finally {
      setSending(false);
    }
  };

  const reopen = async () => {
    if (reopenReason.trim().length < 3) return;
    setReopening(true);
    try {
      setC(await reopenSupportCase(id, reopenReason.trim()));
      setReopenReason('');
      setShowReopen(false);
      toast.success('Case reopened');
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't reopen this case."));
    } finally {
      setReopening(false);
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-muted">
        <Header />
        <div className="lk-container flex-1 py-20">
          <EmptyState
            icon={Ticket}
            title="Sign in to see this case"
            action={
              <button type="button" onClick={openAuthModal} className="lk-btn-primary">
                Log in
              </button>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-muted">
        <Header />
        <div className="lk-container flex-1 py-20">
          <EmptyState
            icon={AlertCircle}
            title="Case not found"
            message={loadError}
            action={
              <Link to="/support" className="lk-btn-primary">
                Back to support
              </Link>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  if (!c) {
    return (
      <div className="min-h-screen bg-surface-muted">
        <Header />
        <div className="lk-container max-w-3xl space-y-4 py-8">
          <Shimmer className="h-24 rounded-2xl" />
          <Shimmer className="h-72 rounded-2xl" />
        </div>
      </div>
    );
  }

  const st = caseStatus(c.status);
  const closed = isClosedStatus(c.status);
  const messages = c.messages || [];

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />
      <main className="flex-1">
        <div className="lk-container max-w-3xl py-6">
          <Link to="/support" className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-ink-500 hover:text-ink-900">
            <ArrowLeft className="h-4 w-4" />
            All support cases
          </Link>

          <div className="lk-card p-4 sm:p-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${st.tone}`}>{st.label}</span>
              {c.isEventDay && (
                <span className="inline-flex items-center gap-1 rounded-full bg-danger-tint px-2.5 py-0.5 text-xs font-semibold text-danger">
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" /> Urgent
                </span>
              )}
              <span className="text-xs text-ink-500">
                {c.caseNumber} · opened {dateTime(c.createdAt)}
              </span>
            </div>
            <h1 className="mt-2 text-h2 text-ink-900">{c.subject}</h1>
            {c.categoryLabel && <p className="mt-0.5 text-sm text-ink-500">{c.categoryLabel}</p>}
            {st.hint && <p className="mt-3 rounded-xl bg-surface-muted px-3 py-2 text-sm text-ink-700">{st.hint}</p>}
            {c.resolutionNotes && closed && (
              <p className="mt-2 rounded-xl bg-success-tint px-3 py-2 text-sm text-success">
                <span className="font-semibold">Resolution: </span>
                {c.resolutionNotes}
              </p>
            )}

            {c.booking && (
              <Link to={`/booking/${c.booking.id}`} className="lk-focus mt-3 flex items-center gap-3 rounded-xl ring-1 ring-ink-200 px-3 py-2.5 hover:bg-surface-muted">
                <Ticket className="h-5 w-5 shrink-0 text-ink-400" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-ink-900">{c.booking.eventName || `Booking #${c.booking.id}`}</span>
                  <span className="flex items-center gap-1 text-xs text-ink-500">
                    <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                    {c.booking.eventDate ? dateTime(c.booking.eventDate) : `Booking #${c.booking.id}`}
                    {c.booking.totalPrice != null && ` · ₹${formatINR(c.booking.totalPrice)}`}
                  </span>
                </span>
              </Link>
            )}
          </div>

          <div className="mt-4 grid gap-4">
            <div className="lk-card p-4 sm:p-5">
              <ul className="space-y-4" aria-live="polite">
                {/* The description opens the thread, unless the backend already
                    stored it as the first message. */}
                {c.description && !(messages[0]?.senderType === 'customer' && messages[0]?.message === c.description) && (
                  <MessageBubble m={{ senderType: 'customer', mine: true, message: c.description, createdAt: c.createdAt, attachments: [] }} />
                )}
                {messages.map((m) => (
                  <MessageBubble key={m.id} m={m} />
                ))}
              </ul>
              <div ref={endRef} />

              {!closed ? (
                <form onSubmit={send} className="mt-4 border-t border-ink-200/70 pt-4">
                  {attachments.length > 0 && (
                    <div className="mb-2">
                      <AttachmentPicker value={attachments} onChange={setAttachments} disabled={sending} />
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    {attachments.length === 0 && <AttachmentPicker value={attachments} onChange={setAttachments} disabled={sending} compact />}
                    <label htmlFor="reply" className="sr-only">Reply</label>
                    <textarea
                      id="reply"
                      rows={1}
                      className="lk-input min-h-[44px] flex-1 resize-y py-2.5"
                      placeholder="Write a reply…"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      maxLength={4000}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(e);
                      }}
                    />
                    <button
                      type="submit"
                      disabled={sending || (!message.trim() && !attachments.length)}
                      aria-label="Send"
                      className="lk-focus flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-primary text-white disabled:opacity-40"
                    >
                      {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                    </button>
                  </div>
                </form>
              ) : (
                c.canReopen && (
                  <div className="mt-4 border-t border-ink-200/70 pt-4">
                    {!showReopen ? (
                      <button type="button" onClick={() => setShowReopen(true)} className="lk-btn-ghost w-full text-sm">
                        <RotateCcw className="h-4 w-4" />
                        Still need help? Reopen this case
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <label htmlFor="reopen-reason" className="block text-sm font-medium text-ink-700">What's still not right?</label>
                        <textarea id="reopen-reason" className="lk-input min-h-[5rem] resize-y" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} maxLength={1000} />
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setShowReopen(false)} className="lk-btn-ghost flex-1">
                            Cancel
                          </button>
                          <button type="button" onClick={reopen} disabled={reopening || reopenReason.trim().length < 3} className="lk-btn-primary flex-1 disabled:opacity-50">
                            {reopening && <Loader2 className="h-4 w-4 animate-spin" />}
                            Reopen
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}
            </div>

            <RefundSection c={c} onChanged={() => load(true)} />
            {closed && <RatingBox c={c} onChanged={() => load(true)} />}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SupportCase;
